import { useEffect, useState } from 'react';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useSiteData } from '@/context/SiteDataContext';

export function LinkRows({ value, onChange, testid }) {
  const [dragIdx, setDragIdx] = useState(null);
  const upd = (i, k, v) => onChange(value.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const drop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    const next = [...value];
    const [m] = next.splice(dragIdx, 1);
    next.splice(i, 0, m);
    onChange(next);
    setDragIdx(null);
  };
  return (
    <div className="space-y-2">
      {value.map((l, i) => (
        <div key={i} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)}
          data-testid={`${testid}-row-${i}`}
          className={`flex flex-wrap items-center gap-2 bg-white border rounded p-2 ${dragIdx === i ? 'opacity-50' : ''}`}>
          <GripVertical className="w-4 h-4 text-charcoal-muted cursor-grab" />
          <input value={l.label || ''} onChange={(e) => upd(i, 'label', e.target.value)} placeholder="Label" data-testid={`${testid}-label-${i}`} className="flex-1 min-w-[120px] px-2 py-1.5 border rounded text-sm" />
          <input value={l.href || ''} onChange={(e) => upd(i, 'href', e.target.value)} placeholder="/page or https://…" data-testid={`${testid}-href-${i}`} className="flex-1 min-w-[160px] px-2 py-1.5 border rounded text-xs font-mono" />
          <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={l.visible !== false} onChange={(e) => upd(i, 'visible', e.target.checked)} data-testid={`${testid}-visible-${i}`} /> Show</label>
          <button onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`${testid}-delete-${i}`} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={() => onChange([...value, { label: 'New link', href: '/', visible: true }])} data-testid={`${testid}-add`}
        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-royal text-white"><Plus className="w-3 h-3" /> Add link</button>
    </div>
  );
}

// Loads a link array from site settings, edits locally, saves on click.
export default function LinkListPanel({ settingsKey, defaults, title, help, testid, onSaved }) {
  const { settings, refresh } = useSiteData();
  const [links, setLinks] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (links === null && settings) setLinks(settings[settingsKey] || defaults);
  }, [settings, settingsKey, defaults, links]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/site/settings', { [settingsKey]: links });
      await refresh(['settings']);
      toast.success('Links saved');
      onSaved?.();
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };

  if (!links) return null;
  return (
    <div className="bg-[#F8F9FA] border rounded-lg p-4" data-testid={testid}>
      {title && <div className="font-serif text-lg text-royal font-bold">{title}</div>}
      {help && <div className="text-xs text-charcoal-muted mb-3">{help}</div>}
      <LinkRows value={links} onChange={setLinks} testid={testid} />
      <div className="mt-3 flex justify-end">
        <button onClick={save} disabled={busy} data-testid={`${testid}-save`} className="btn-royal text-sm py-2 px-4">{busy ? 'Saving…' : 'Save links'}</button>
      </div>
    </div>
  );
}
